import { GeneralResMock } from '../types/general.type'

import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { HmoResMock, HmoTariffResMock } from '../types/hmo.type'

type Props = {
  page?: number | string
  limit?: number | string
  query?: string
  sortDir?: 'asc' | 'desc'
  id?: string
  tariffable_type?: string
  hmo_plan_id?: string
  hmo_id?: string
  status?: string
}
export const useHmoTarrif = ({ id, limit, page, query, sortDir, tariffable_type, hmo_plan_id, status }: Props = {}) => {
  const { data: hmoTarrifs, isLoading: hmoTarrifIsLoading } = useQuery({
    queryFn: async () => {
      const endpoint = 'hmo_tariffs'
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<HmoTariffResMock[], HmoResMock[]>>(endpoint, {
        params: {
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(query && { query }),
          ...(sortDir && { sort_dir: sortDir }),
          ...(tariffable_type && { tariffable_type }),
          ...(hmo_plan_id && { hmo_plan_id }),
          ...(status && { status })
        }
      })
      return data
    },
    queryKey: ['hmoTarrifs', { limit, page, query, sortDir, hmo_plan_id, tariffable_type, status }]
  })

  const { data: singleHmoTarrif, isLoading: singleHmoTarrifIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<HmoTariffResMock>>(`hmo_tariffs/${id}`)
      return data
    },
    queryKey: ['hmoTarrif', { id: id }]
  })

  const { mutateAsync: createTarrif } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`hmo_tariffs`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmoTarrifs'] })
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
      queryClient.invalidateQueries({ queryKey: ['hmo_plans'] })
    }
  })

  const { mutateAsync: updateTarrif } = useMutation({
    mutationFn: async ({ data, id }: { data: any; id: string }) => {
      const { data: updatedData } = await AXIOS_INSTANCE.patch(`hmo_tariffs/${id}`, data, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return updatedData
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['hmoTarrifs'] })
      queryClient.invalidateQueries({ queryKey: ['hmo_plans'] })
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
      queryClient.invalidateQueries({ queryKey: ['hmo_tariffs', { id }] })
    }
  })

  const { mutateAsync: deleteTariff } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`hmo_tariffs/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmoTarrifs'] })
      queryClient.invalidateQueries({ queryKey: ['hmo_plans'] })
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
    }
  })

  return {
    createTarrif,
    deleteTariff,
    hmoPagination: hmoTarrifs?.meta.pagination,
    hmoTariffIncluded: hmoTarrifs?.included,
    singleHmoTarrif,
    singleHmoTarrifIsLoading,
    hmoTarrifIsLoading,
    hmoTarrifs: hmoTarrifs?.data,
    hmosIncluded: hmoTarrifs?.included,
    updateTarrif
  }
}
